/*
    defines field model
    initValue -
        initial value for field
    defaultValue -
        value to be submitted in form if value is empty
    type -
        oneOf FIELD.TYPE
    name -
        used for field id/name
        also used as form field name
    label -
        used for sr-only, and display label (if any)
    placeholder -
        input field placeholder
        backup for sr-only, if label not present
    formatter -
        if defined, formatted field executes and fetched formatted value
        only for display purpose
    valueSetter -
        can be defined to restrict characters
        invoked by setValue method
    validations -
        defines validation methods for the field

    setValue -
        arguments - value and event(optional)
        default sets value property
        if valueSetter is defined, it is invoked, and returned value is set
    formatted -
        if any formatter is defined, calls the method and returns formatted value
        only for display purpose
    validate -
        runs all validations, if any errors, returns false
        error array is filled with function names of failed validations
*/

import { FIELD } from "../../constants/formConstants";
import validationFn from "./validations";

function _getDefaultValue(type) {
  let defaultValue = "";
  switch (type) {
    case FIELD.TYPE.CHECKBOXGROUP:
      defaultValue = [];
      break;
  }
  return defaultValue;
}

function Field({
  type,
  name, //mandatory
  defaultValue,
  maxLength,
  minLength,
  required,
  validations,
  formatter,
  valueSetter,
  ...rest
}) {
  let _formatter = formatter;
  let _valueSetter = valueSetter;
  let _customErrors = [];

  Object.assign(this, {
    type,
    name,
    defaultValue,
    value: defaultValue || _getDefaultValue(type),
    maxLength,
    minLength,
    required,
    validations: validations || [],
    errors: [],
    ...rest,
  });

  this.setValue = (value, e) => {
    if (_valueSetter) {
      value = _valueSetter(value, e);
    }
    this.value = value;
  };

  this.resetValue = () => {
    this.value = defaultValue || _getDefaultValue(type);
  };

  //return type string
  this.formatted = () => {
    if (!_formatter) return this.value;
    return _formatter(this);
  };

  //runs all validations
  this.validate = () => {
    //if no validations configured, validate to true
    if (
      !this.validations.length ||
      (typeof this.validateIf === "function" && !this.validateIf())
    ) {
      this.errors = [];
      return true;
    }

    //run all validations
    this.errors = this.validations
      .map((v) => {
        var execFn = null;
        execFn = typeof v === "function" ? v : validationFn[v];
        if (!execFn(this)) {
          return execFn.id;
        }
        return null;
      })
      .filter((v) => v !== null);
    this.errors = [...this.errors, ..._customErrors];
    return this.errors.length === 0;
  };

  this.resetErrors = () => {
    _customErrors = [];
    this.errors = [];
  };

  this.resetCustomErrors = () => {
    _customErrors = [];
  };

  this.addCustomError = (customError) => {
    _customErrors.push(customError);
    this.errors.push(customError);
  };

  this.defFormatter = (fn) => (_formatter = fn);
  this.defValueSetter = (fn) => (_valueSetter = fn);

  return this;
}

//settings accepts all parameters as supported by Field constructor
const createField = (settings) => {
  return new Field(settings);
};

export { createField };
